import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { Button } from '@hero/web-components';
import { DownloadIcon, CloseIcon } from '@hero/web-icons';

export interface OrderBulkActionsProps {
  selectedOrderIds: string[];
  orders: Order[];
  onExport?: (orderIds: string[]) => Promise<void>;
  onCancel: () => void;
  isExporting?: boolean;
}

/**
 * Bulk Actions Component for Customer Dashboard
 * Clean, modern design focused on export functionality
 * Shows when orders are selected
 */
export const OrderBulkActions: React.FC<OrderBulkActionsProps> = ({
  selectedOrderIds,
  orders,
  onExport,
  onCancel,
  isExporting = false,
}) => {
  const { t } = useTranslation('m-order');

  if (selectedOrderIds.length === 0) return null;

  const handleExport = async () => {
    if (!onExport) return;
    try {
      await onExport(selectedOrderIds);
    } catch (error) {
      console.error('Failed to export orders:', error);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">
              {t('table.bulk_actions.selected', { count: selectedOrderIds.length })}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isExporting}
              className="flex items-center gap-2">
              <CloseIcon className="w-4 h-4" />
              {t('table.bulk_actions.cancel')}
            </Button>

            {onExport && (
              <Button
                variant="default"
                size="sm"
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90">
                <DownloadIcon className="w-4 h-4" />
                {isExporting
                  ? t('table.bulk_actions.exporting')
                  : t('table.bulk_actions.export')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
